import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Text } from '@adobe/react-spectrum';
import Add from '@spectrum-icons/workflow/Add';
import Edit from '@spectrum-icons/workflow/Edit';
import AdditionalInfoPopup from '../../AdditionalInfoPopup';
import styles from './AdditionalInfo.module.scss';
import { EditOnlyWrapper } from '../context/RBACContext';

interface LinkItem {
  id: string;
  title: string;
  url: string;
}

interface AdditionalInfoProps {
  links: LinkItem[];
  isExpanded?: boolean; // Control visibility of additional info links list
  onAddLink?: (link: Omit<LinkItem, 'id'>) => Promise<unknown>;
  onEditLink?: (link: LinkItem) => Promise<unknown>;
  onDeleteLink?: (linkId: string) => Promise<unknown>;
  issueId?: string; // Add issueId for API calls
  onDataRefresh?: (patchResponse?: unknown) => void; // Add callback for seamless data refresh
}

// Helper function to normalize URLs with proper protocol
const normalizeUrl = (url: string): string => {
  if (!url) return 'No url';

  // If URL already has a protocol, return as is (case-insensitive)
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // For all other URLs (including www.example.com), add https://
  if (url.startsWith('www.') || url.includes('.')) {
    return `https://${url}`;
  }

  return url;
};

export interface AdditionalInfoRef {
  openAddPopup: () => void;
}

const AdditionalInfo = forwardRef<AdditionalInfoRef, AdditionalInfoProps>(
  (
    {
      links,
      isExpanded = true,
      onAddLink,
      onEditLink,
      onDeleteLink,
      issueId,
      onDataRefresh
    },
    ref
  ) => {
    // Ensure links is always an array to prevent map errors
    const safeLinks = Array.isArray(links) ? links : [];
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState<LinkItem | null>(null);

    const handleOpenAddPopup = () => {
      setSelectedLink(null);
      setIsPopupOpen(true);
    };

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      openAddPopup: handleOpenAddPopup
    }));

    const handleOpenEditPopup = (link: LinkItem) => {
      setSelectedLink(link);
      setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
      setIsPopupOpen(false);
      setSelectedLink(null);
    };

    const handleSaveLink = async (linkData: {
      description: string;
      url: string;
    }) => {
      try {
        let patchResponse: unknown;
        if (selectedLink) {
          // Editing existing link
          if (onEditLink) {
            patchResponse = await onEditLink({
              id: selectedLink.id,
              title: linkData.description,
              url: linkData.url
            });
          }
        } else {
          // Adding new link
          if (onAddLink) {
            patchResponse = await onAddLink({
              title: linkData.description,
              url: linkData.url
            });
          }
        }

        // Prefer PATCH envelope (moment + notifications) to avoid redundant GETs
        if (onDataRefresh) {
          onDataRefresh(patchResponse);
        }

        // Close popup after successful save
        handleClosePopup();
      } catch (error) {
        // Keep popup open on error so user can retry
      }
    };

    const handleDeleteLink = async () => {
      if (!selectedLink || !onDeleteLink) return;

      try {
        const patchResponse = await onDeleteLink(selectedLink.id);
        if (onDataRefresh) {
          onDataRefresh(patchResponse);
        }
        handleClosePopup();
      } catch (error) {
        // Keep popup open on error so user can retry
      }
    };

    return (
      <div
        className={styles.additionalInfo}
        data-testid="additional-info-section"
      >
        {/* Only show additional info links list when expanded */}
        {isExpanded && (
          <div
            className={styles.linksList}
            data-testid="additional-info-links-list"
          >
            {safeLinks.length === 0 ? (
              <div
                className={styles.emptyState}
                data-testid="additional-info-empty-state"
              >
                <p className={styles.emptyMessage}>
                  No additional info links have been added yet.
                </p>
                <EditOnlyWrapper>
                  <button
                    className={styles.addFirstLinkButton}
                    onClick={handleOpenAddPopup}
                    data-testid="additional-info-add-first-button"
                  >
                    Add Link
                  </button>
                </EditOnlyWrapper>
              </div>
            ) : (
              safeLinks.map(link => (
                <div
                  key={link.id}
                  className={styles.linkItem}
                  data-testid={`additional-info-link-${link.title
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  <a
                    href={normalizeUrl(link.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.linkTitle}
                    data-testid={`additional-info-link-url-${link.title
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                  >
                    <Text
                      UNSAFE_className={styles.linkTitle}
                      data-testid={`additional-info-link-title-${link.title
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      {link.title}
                    </Text>
                  </a>
                  <EditOnlyWrapper>
                    <button
                      className={styles.editButton}
                      onClick={() => handleOpenEditPopup(link)}
                      data-testid={`additional-info-edit-button-${link.title
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      <div
                        className={styles.editIcon}
                        data-testid={`additional-info-edit-icon-${link.title
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                      >
                        <Edit size="S" />
                      </div>
                    </button>
                  </EditOnlyWrapper>
                </div>
              ))
            )}
          </div>
        )}

        {/* Additional Info Popup */}
        <AdditionalInfoPopup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          onSave={handleSaveLink}
          onDelete={handleDeleteLink}
          editMode={!!selectedLink}
          initialValues={
            selectedLink
              ? {
                  id: selectedLink.id,
                  description: selectedLink.title,
                  url: selectedLink.url
                }
              : undefined
          }
          issueId={issueId}
          existingLinks={safeLinks.map(link => ({
            id: link.id,
            title: link.title,
            url: link.url
          }))}
          data-testid="additional-info-popup"
        />
      </div>
    );
  }
);

AdditionalInfo.displayName = 'AdditionalInfo';

export default AdditionalInfo;
